import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTimeMediumDotted } from '@klareospace/pharos-lib-front-utils';
import { getFullName } from '@klareospace/pharos-lib-utils';
import { TASK_PROOF_TYPE, type TaskProof } from '@klareospace/pharos-lib-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, ImageGallery } from '@klareospace/pharos-lib-web-utils';

import { buildProofViewTitle, type JobProof } from '../utils/proof-labels';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProofViewDialogProps {
  /** The job/proof pair to display, or null to keep the dialog closed. */
  readonly jobProof: JobProof | null;
  readonly onOpenChange: (open: boolean) => void;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface ProofFooterProps {
  readonly proof: TaskProof;
}

function ProofFooter({ proof }: ProofFooterProps): React.JSX.Element {
  const { i18n } = useTranslation('tasks');

  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>{getFullName(proof.createdBy.firstName, proof.createdBy.lastName)}</span>
      <span>{formatDateTimeMediumDotted(proof.createdAt, i18n.language)}</span>
    </div>
  );
}

interface ProofContentProps {
  readonly lightboxTitle: string;
  readonly proof: TaskProof;
}

/** Renders the proof body by type: text block (comment), single image (signature), or gallery (images). */
function ProofContent({ lightboxTitle, proof }: ProofContentProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  if (proof.type === TASK_PROOF_TYPE.COMMENT) {
    return <div className="w-full rounded-md bg-muted px-4 py-3 text-sm text-foreground">{proof.comment}</div>;
  }

  if (proof.type === TASK_PROOF_TYPE.SIGNATURE) {
    return (
      <div className="w-full rounded-md bg-muted p-3">
        <img alt={t('proofs.type.signature')} className="mx-auto max-h-64 object-contain" src={proof.signature} />
      </div>
    );
  }

  return (
    <ImageGallery
      closeAriaLabel={t('proofs.close')}
      downloadAriaLabel={(index): string => t('proofs.downloadPhoto', { index: index + 1 })}
      downloadTitle={t('proofs.downloadTitle')}
      images={proof.images.map((image, index) => ({
        alt: t('proofs.downloadPhoto', { index: index + 1 }),
        downloadFileName: `proof-${index + 1}.jpg`,
        src: image.originalUrl,
        thumbnailSrc: image.thumbnailUrl,
      }))}
      lightboxTitle={lightboxTitle}
      nextAriaLabel={t('proofs.nextPhoto')}
      previousAriaLabel={t('proofs.previousPhoto')}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared read-only viewer dialog for a single proof, opened from `ProofsTable`'s
 * "View" action in both `OrderProofsTab` and `TaskProofsSection`. Same shell for
 * all three proof types (title + content + footer) — only the content differs:
 * comment renders as text, signature as a single image, images as a gallery
 * with lightbox.
 */
export function ProofViewDialog({ jobProof, onOpenChange }: ProofViewDialogProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const title = jobProof !== null ? buildProofViewTitle(jobProof, t) : '';

  return (
    <Dialog onOpenChange={onOpenChange} open={jobProof !== null}>
      <DialogContent className="max-w-md">
        {jobProof !== null && (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <ProofContent lightboxTitle={title} proof={jobProof.proof} />
              <ProofFooter proof={jobProof.proof} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
